import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Mail, MessageSquare, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import podcastsData from '../data/podcasts.json';

// Animated Input Component
const FormInput = ({ label, type = 'text', name, value, onChange, required, placeholder }) => {
  const [isFocused, setIsFocused] = useState(false);

  return (
    <div className="relative">
      <label
        htmlFor={name}
        className={`absolute left-4 transition-all duration-200 pointer-events-none ${
          isFocused || value
            ? '-top-2.5 text-xs text-accent bg-[var(--bg-secondary)] px-2'
            : 'top-4 text-[var(--text-muted)]'
        }`}
      >
        {label}
      </label>
      <input
        type={type}
        id={name}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={isFocused ? placeholder : ''}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        className="w-full px-4 py-4 bg-[var(--bg-tertiary)] border border-[var(--bg-elevated)] rounded-xl
                   text-[var(--text-primary)] placeholder-[var(--text-muted)]
                   focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/20
                   transition-all duration-200"
      />
    </div>
  );
};

// Animated Select Component
const FormSelect = ({ label, name, value, onChange, required, options }) => {
  const [isFocused, setIsFocused] = useState(false);

  return (
    <div className="relative">
      <label
        htmlFor={name}
        className={`absolute left-4 transition-all duration-200 pointer-events-none z-10 ${
          isFocused || value
            ? '-top-2.5 text-xs text-accent bg-[var(--bg-secondary)] px-2'
            : 'top-4 text-[var(--text-muted)]'
        }`}
      >
        {label}
      </label>
      <select
        id={name}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        className="w-full px-4 py-4 bg-[var(--bg-tertiary)] border border-[var(--bg-elevated)] rounded-xl
                   text-[var(--text-primary)] appearance-none cursor-pointer
                   focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/20
                   transition-all duration-200"
      >
        <option value="" disabled></option>
        {options.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {/* Dropdown Arrow */}
      <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
        <svg className="w-5 h-5 text-[var(--text-muted)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </div>
  );
};

// Animated Textarea Component
const FormTextarea = ({ label, name, value, onChange, required, rows = 6, placeholder }) => {
  const [isFocused, setIsFocused] = useState(false);

  return (
    <div className="relative">
      <label
        htmlFor={name}
        className={`absolute left-4 transition-all duration-200 pointer-events-none ${
          isFocused || value
            ? '-top-2.5 text-xs text-accent bg-[var(--bg-secondary)] px-2'
            : 'top-4 text-[var(--text-muted)]'
        }`}
      >
        {label}
      </label>
      <textarea
        id={name}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        rows={rows}
        placeholder={isFocused ? placeholder : ''}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        className="w-full px-4 py-4 bg-[var(--bg-tertiary)] border border-[var(--bg-elevated)] rounded-xl
                   text-[var(--text-primary)] placeholder-[var(--text-muted)] resize-none
                   focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/20
                   transition-all duration-200"
      />
    </div>
  );
};

const ContactPage = () => {
  const { podcastInfo } = podcastsData;
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    category: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch('/.netlify/functions/send-email', {
        method: 'POST',
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', category: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const categoryOptions = [
    { value: 'Questions', label: 'Questions' },
    { value: 'Episode Suggestions', label: 'Episode Suggestions' },
    { value: 'General Inquiry', label: 'General Inquiry' },
    { value: 'Partnership', label: 'Partnership & Collaboration' },
  ];

  return (
    <div className="min-h-screen bg-[var(--bg-primary)] text-[var(--text-primary)]">
      <Helmet>
        <title>Contact Us | News From The Pew</title>
        <meta name="description" content="Get in touch with News From The Pew. Send us your questions, feedback, or prayer requests. We'd love to hear from you!" />
        <link rel="canonical" href="https://newsfromthepew.com/contact" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://newsfromthepew.com/contact" />
        <meta property="og:title" content="Contact Us | News From The Pew" />
        <meta property="og:description" content="Get in touch with News From The Pew. Send us your questions, feedback, or prayer requests." />
        <meta property="og:image" content="https://newsfromthepew.com/assets/images/default_branding_image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Contact Us | News From The Pew" />
        <meta name="twitter:description" content="Get in touch with News From The Pew. Send us your questions, feedback, or prayer requests." />
        <meta name="twitter:image" content="https://newsfromthepew.com/assets/images/default_branding_image.png" />
      </Helmet>

      <Navigation />

      <main className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20 overflow-hidden">
        {/* Ambient glows */}
        <div className="ambient-orb-primary -top-20 -left-20 w-72 h-72" />
        <div className="ambient-orb-secondary -bottom-32 -right-32 w-80 h-80" />

        <div className="relative grid lg:grid-cols-5 gap-12 lg:gap-16">
          {/* Left Column - Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-2"
          >
            <h1 className="font-display text-display-md md:text-display-lg text-[var(--text-primary)] mb-6">
              Get in Touch
            </h1>

            <div className="h-1 w-24 bg-gradient-primary rounded-full mb-8" />

            <p className="text-[var(--text-secondary)] text-lg mb-8 leading-relaxed">
              Have a question? Topic suggestion? We'd love to hear from you.
              Fill out the form and we'll get back to you as soon as possible.
            </p>

            {/* Contact Info Cards */}
            <div className="space-y-4">
              {/* Email Card */}
              <motion.a
                href={`mailto:${podcastInfo.email}`}
                whileHover={{ scale: 1.02, x: 4 }}
                className="glow-card flex items-center gap-4 p-4 bg-[var(--bg-secondary)] rounded-xl border border-[var(--bg-tertiary)]
                           hover:border-accent/30 transition-all duration-200 group"
              >
                <div className="flex items-center justify-center w-12 h-12 rounded-full bg-accent/10
                                group-hover:bg-accent/20 transition-colors">
                  <Mail size={20} className="text-accent" />
                </div>
                <div>
                  <p className="text-[var(--text-muted)] text-sm">Email us directly</p>
                  <p className="text-[var(--text-primary)] font-medium group-hover:text-accent transition-colors">
                    {podcastInfo.email}
                  </p>
                </div>
              </motion.a>

              {/* Response Time Card */}
              <div className="glow-card flex items-center gap-4 p-4 bg-[var(--bg-secondary)] rounded-xl border border-[var(--bg-tertiary)]">
                <div className="flex items-center justify-center w-12 h-12 rounded-full bg-accent/10">
                  <MessageSquare size={20} className="text-accent" />
                </div>
                <div>
                  <p className="text-[var(--text-muted)] text-sm">Response time</p>
                  <p className="text-[var(--text-primary)] font-medium">Usually within 24-48 hours</p>
                </div>
              </div>
            </div>

            {/* Decorative Element */}
            <div className="hidden lg:block mt-12 relative">
              <div className="ambient-orb-tertiary -left-4 top-0 w-40 h-40" />
              <div className="ambient-orb-tertiary left-20 top-12 w-32 h-32 opacity-50" />
            </div>
          </motion.div>

          {/* Right Column - Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:col-span-3 relative"
          >
            {/* Form background glow */}
            <div className="ambient-orb-tertiary top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full opacity-30" />

            <div className="relative bg-[var(--bg-secondary)] rounded-2xl p-6 md:p-8 border border-[var(--bg-tertiary)]">
              <AnimatePresence mode="wait">
                {submitStatus === 'success' ? (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    className="text-center py-12"
                  >
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: 'spring', delay: 0.2 }}
                      className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-500/10 mb-6"
                    >
                      <CheckCircle size={40} className="text-green-500" />
                    </motion.div>
                    <h3 className="font-display text-2xl text-[var(--text-primary)] mb-3">
                      Message Sent!
                    </h3>
                    <p className="text-[var(--text-secondary)] mb-6">
                      Thank you for reaching out. We'll get back to you soon.
                    </p>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setSubmitStatus(null)}
                      className="px-6 py-3 bg-[var(--bg-tertiary)] hover:bg-[var(--bg-elevated)] rounded-xl
                                 text-[var(--text-primary)] transition-colors"
                    >
                      Send Another Message
                    </motion.button>
                  </motion.div>
                ) : (
                  <motion.form
                    key="form"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onSubmit={handleSubmit}
                    className="space-y-6"
                  >
                    {/* Error Message */}
                    <AnimatePresence>
                      {submitStatus === 'error' && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/20 rounded-xl"
                        >
                          <AlertCircle size={20} className="text-red-500 flex-shrink-0" />
                          <p className="text-red-400 text-sm">
                            Something went wrong. Please try again later.
                          </p>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {/* Name & Email Row */}
                    <div className="grid md:grid-cols-2 gap-6">
                      <FormInput
                        label="Name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="John Doe"
                      />
                      <FormInput
                        label="Email"
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="john@example.com"
                      />
                    </div>

                    {/* Category */}
                    <FormSelect
                      label="Category"
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                      required
                      options={categoryOptions}
                    />

                    {/* Message */}
                    <FormTextarea
                      label="Message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      placeholder="Tell us what's on your mind..."
                    />

                    {/* Submit Button */}
                    <motion.button
                      whileHover={{ scale: 1.01, y: -2 }}
                      whileTap={{ scale: 0.99 }}
                      type="submit"
                      disabled={isSubmitting}
                      className={`w-full flex items-center justify-center gap-3 px-6 py-4
                                  bg-gradient-subtle text-white font-semibold rounded-xl
                                  shadow-glow hover:shadow-glow-lg transition-all duration-300
                                  ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 size={20} className="animate-spin" />
                          <span>Sending...</span>
                        </>
                      ) : (
                        <>
                          <Send size={20} />
                          <span>Send Message</span>
                        </>
                      )}
                    </motion.button>

                    <p className="text-[var(--text-muted)] text-xs text-center">
                      By submitting this form, you agree to our privacy policy.
                    </p>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ContactPage;
